// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Navigate } from 'react-router-dom';
import { RequireAuth, useSharedSession } from '../shared/auth';
import { FlagRoute, useRolloutHidEverySite } from '../shared/featureFlags';
import AdminCenterShell from './components/AdminCenterShell';

/**
 * Gate for the admin-center-enabled flag (AB#29037) — deliberately placed
 * inside `RequireAuth`, not at App.tsx's route level. An earlier version
 * decided the flag before auth resolved: an unauthenticated deep link
 * evaluated the anonymous context (always false for this flag), redirected
 * to "/", and lost the URL `RequireAuth`/`ProtectedRoute` would otherwise
 * have preserved through login. Here, by the time this runs, the user is
 * already signed in, so the flag only ever evaluates their real identity
 * (independent review, AB#29037 — this is the fix for that finding).
 *
 * `FlagRoute` waits on flag readiness before deciding: reading the flag's catalog
 * default (always false, for the instant before LaunchDarkly has
 * identified this user) would redirect away a valid bookmark on a cold
 * cache.
 *
 * Precisely what "not ready" renders: a blank surface (`return null`), held
 * open for as long as readiness doesn't resolve — not a fallback to the
 * catalog default's redirect. Those are different outcomes: a black-holed
 * `identify()` (no `LDProvider` init timeout applies to it — see
 * `LaunchDarklyContext.tsx`) leaves this blank indefinitely rather than
 * settling on the off default and redirecting away (PR #366 review,
 * Federico).
 */
function RequireAdminCenterFlag({ children }: { children: React.ReactNode }) {
  return (
    <FlagRoute flag="adminCenterEnabled" fallback={<Navigate to="/" replace />}>
      {children}
    </FlagRoute>
  );
}

/**
 * Coarse role gate for the whole surface. Before this, the route was
 * auth-only — any signed-in user, not just admins, could reach every Admin
 * Center screen by URL once admin-center-enabled turned on, even though the
 * nav entry that links here already checks isAdmin. Not the security
 * boundary (nothing server-side enforces this yet either — see the
 * LaunchDarkly Phase 2 design doc) — this keeps the UI honest the same way
 * shared/auth/RequirePlatformAdmin.tsx does for the provisioning app
 * (independent review, AB#29037).
 */
function RequireAdminAccess({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading } = useSharedSession();

  if (isLoading) return null;
  if (!isAdmin) return <p>You do not have access to Admin Center.</p>;

  return <>{children}</>;
}

/**
 * The site rollout (`site-rollout` flag) covers Admin Center too: with every
 * site hidden there is no scope to administer, so it shows Helios' own empty
 * state instead of the dashboard. An organization with no sites at all is a
 * different case and stays in — that is where a first site gets onboarded.
 */
function RequireEnabledSite({ children }: { children: React.ReactNode }) {
  if (useRolloutHidEverySite()) {
    return (
      <p role="status">
        You currently don&apos;t have any assigned sites. Please contact your administrator.
      </p>
    );
  }
  return <>{children}</>;
}

/**
 * Admin Center's route-level entry point — what App.tsx mounts at
 * /admin-center/*. This is the seam a Module Federation host would import
 * as the remote's exposed module (ADR-0014 Phase 2), so keep it this thin:
 * auth-gate, flag-gate, role-gate, site-gate, then the shell. Everything else lives
 * behind AdminCenterShell.
 */
export default function AdminCenterApp() {
  return (
    <RequireAuth>
      <RequireAdminCenterFlag>
        <RequireAdminAccess>
          <RequireEnabledSite>
            <AdminCenterShell />
          </RequireEnabledSite>
        </RequireAdminAccess>
      </RequireAdminCenterFlag>
    </RequireAuth>
  );
}
